import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { ArrowRight, Check, ChevronLeft, ChevronRight, FileText, Loader2, PenLine, X } from "lucide-react";

/* A client filling in and signing one of their agent's forms.

   The signature is typed, not drawn -- the same bargain DocuSign and Dotloop
   make. You type your name, you see it written out, you adopt it, and from
   then on it goes wherever the agent placed a signature or initials box.

   The mark is rasterised HERE, in the browser, from the very preview the
   client approved. That is deliberate: a font rendered server-side could
   differ from the one on screen, and then the thing they agreed to would not
   be the thing on the document. Two consequences fall out of it, and both
   are handled below rather than hoped about:

     1. The webfont must actually be loaded before anything is drawn to a
        canvas. If it is not, the canvas silently falls back to a default
        face and the client adopts a signature they never saw. So adoption
        is blocked until document.fonts says the face is ready.

     2. The canvas is bigger than the ink on it. Sent as-is, the signature
        would be fitted to its box by its empty margins and come out small
        and floating. So the image is cropped to its ink before it leaves. */

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Great+Vibes&family=Dancing+Script:wght@600" +
  "&family=Caveat:wght@600&display=swap";

/* Signatures have to be cursive, so the fallbacks are cursive too.

   The webfont is the first choice, but a client on a network that blocks
   Google Fonts must not end up signing in plain sans. Every name after it is
   a cursive face that ships with an operating system -- Snell Roundhand and
   Apple Chancery on macOS and iOS, Segoe Script and Lucida Handwriting on
   Windows, Brush Script on both -- with the generic `cursive` keyword last,
   which the browser maps to whatever handwriting face the device has. */
const CURSIVE_TAIL =
  '"Snell Roundhand", "Apple Chancery", "Segoe Script", "Bradley Hand", ' +
  '"Lucida Handwriting", "Brush Script MT", "Z003", cursive';

/* ...and `cursive` at the end of that list is NOT a guarantee. Measured in
   Chrome on a machine with no handwriting face installed, the generic
   `cursive` keyword renders identically to `serif`. A client whose network
   blocks Google Fonts could therefore adopt a serif signature and never
   notice. So rather than trust the stack, measure it: if the text comes out
   the same width as the default faces, nothing cursive is being used, and
   the client is told instead of being allowed to sign in the wrong hand. */
function cursiveIsReal(style, px = 60) {
  try {
    const c = document.createElement("canvas").getContext("2d");
    const probe = "Signature Mg";
    c.font = `${px}px ${stackFor(style)}`;
    const mine = c.measureText(probe).width;
    c.font = `${px}px serif`;
    const serif = c.measureText(probe).width;
    c.font = `${px}px sans-serif`;
    const sans = c.measureText(probe).width;
    return Math.abs(mine - serif) > 0.5 && Math.abs(mine - sans) > 0.5;
  } catch {
    return false;
  }
}

const STYLES = {
  vibes: { label: "Formal", family: "Great Vibes", size: 1.0 },
  dancing: { label: "Flowing", family: "Dancing Script", size: 0.82 },
  caveat: { label: "Casual", family: "Caveat", size: 0.86 },
};

function stackFor(style) {
  const spec = STYLES[style] || STYLES.vibes;
  return `"${spec.family}", ${CURSIVE_TAIL}`;
}

const MAX_EDGE = 2200;          // the server rejects anything wider
const muted = { color: "var(--text-muted)" };

function ensureFonts() {
  if (document.getElementById("pl-cursive-fonts")) return;
  const l = document.createElement("link");
  l.id = "pl-cursive-fonts";
  l.rel = "stylesheet";
  l.href = FONT_HREF;
  document.head.appendChild(l);
}

function initialsOf(name) {
  const skip = new Set(["and", "&", "or", "the", "of", "de", "van", "von"]);
  return (name || "")
    .trim()
    .split(/[\s-]+/)
    .filter((p) => p && !skip.has(p.toLowerCase().replace(/\./g, "")))
    .slice(0, 3)
    .map((p) => p[0].toUpperCase())
    .join("");
}

/* Crop a canvas to its ink. Without this a signature arrives padded with
   transparency and gets scaled to fit the padding rather than the writing. */
function trimToInk(c) {
  const ctx = c.getContext("2d");
  const W = c.width, H = c.height;
  const d = ctx.getImageData(0, 0, W, H).data;
  let x0 = W, y0 = H, x1 = -1, y1 = -1;
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      if (d[(y * W + x) * 4 + 3] > 8) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  // No ink at all means the font never arrived or the text was blank. Adopting
  // an invisible signature would be worse than failing loudly.
  if (x1 < 0) throw new Error("nothing-drawn");
  const m = 2;
  x0 = Math.max(0, x0 - m); y0 = Math.max(0, y0 - m);
  x1 = Math.min(W - 1, x1 + m); y1 = Math.min(H - 1, y1 + m);
  let w = x1 - x0 + 1, h = y1 - y0 + 1;
  let scale = 1;
  if (w > MAX_EDGE || h > MAX_EDGE) scale = MAX_EDGE / Math.max(w, h);
  const out = document.createElement("canvas");
  out.width = Math.max(8, Math.round(w * scale));
  out.height = Math.max(4, Math.round(h * scale));
  out.getContext("2d").drawImage(c, x0, y0, w, h, 0, 0, out.width, out.height);
  return out.toDataURL("image/png");
}

/* The one invariant that matters: the canvas must use the SAME font string as
   the on-screen preview, fallback and all. If the webfont never arrives, the
   preview falls back to the device's cursive face and so does the canvas, and
   what the client approved is still what gets embedded. Written as two
   different strings, the preview would fall back to cursive and the canvas to
   plain sans, and they would sign a mark they had never seen. */
function fontSpec(style, px) {
  const spec = STYLES[style] || STYLES.vibes;
  return `${Math.round(px * spec.size)}px ${stackFor(style)}`;
}

async function renderMark(text, style, px = 76) {
  const font = fontSpec(style, px);
  const spec = STYLES[style] || STYLES.vibes;
  const size = Math.round(px * spec.size);
  // Nudge the webfont along, naming only the primary -- loading a whole stack
  // is not what this call is for. Whether it arrives or not, the rendering
  // below uses the same stack the preview does, so they cannot disagree.
  try {
    if (document.fonts && document.fonts.load) {
      await document.fonts.load(`${size}px "${spec.family}"`, text);
    }
  } catch { /* fall through to whatever cursive face the device has */ }

  const SCALE = 3;
  const pad = Math.ceil(size * 0.3);
  const probe = document.createElement("canvas").getContext("2d");
  probe.font = font;
  const w = Math.ceil(probe.measureText(text).width) + pad * 2;
  const h = Math.ceil(size * 2);

  const c = document.createElement("canvas");
  c.width = Math.max(8, w * SCALE);
  c.height = Math.max(8, h * SCALE);
  const ctx = c.getContext("2d");
  ctx.scale(SCALE, SCALE);
  ctx.font = font;
  ctx.fillStyle = "#17306b";          // ink blue reads as signed, black reads as printed
  ctx.textBaseline = "middle";
  ctx.fillText(text, pad, h / 2);
  return trimToInk(c);
}

/* ------------------------------------------------------- adopting a mark */

export function AdoptSignature({ suggestedName, consentText, onAdopted, onClose }) {
  const [name, setName] = useState(suggestedName || "");
  const [initials, setInitials] = useState(initialsOf(suggestedName));
  const [style, setStyle] = useState("vibes");
  const [ready, setReady] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const touched = useRef(false);

  useEffect(() => { ensureFonts(); }, []);

  /* Wait for the lettering, then check it is genuinely cursive before letting
     anyone adopt it. Signing in the wrong hand without being told would be
     worse than waiting, and worse than an honest failure. */
  const [noCursive, setNoCursive] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setNoCursive(false);
    setReady(false);
    const settle = () => {
      if (!alive) return;
      const ok = Object.keys(STYLES).some((k) => cursiveIsReal(k));
      setNoCursive(!ok);
      setReady(ok);
    };
    const timer = setTimeout(settle, 5000);
    (async () => {
      try {
        if (document.fonts && document.fonts.load) {
          await Promise.all(Object.values(STYLES).map((s) =>
            document.fonts.load(`76px "${s.family}"`, "AaBbCc")));
        }
      } catch { /* measured below either way */ }
      clearTimeout(timer);
      settle();
    })();
    return () => { alive = false; clearTimeout(timer); };
  }, [attempt]);

  const onName = (v) => {
    setName(v);
    if (!touched.current) setInitials(initialsOf(v));
  };

  const adopt = async () => {
    setErr("");
    if (name.trim().length < 2) { setErr("Please type your full name."); return; }
    if (!initials.trim()) { setErr("Please give your initials."); return; }
    if (!agreed) { setErr("Please tick the box to agree."); return; }
    setBusy(true);
    try {
      const signature_png = await renderMark(name.trim(), style);
      const initials_png = await renderMark(initials.trim(), style);
      const { data } = await api.post("/me/signature", {
        name: name.trim(), initials: initials.trim(), style,
        signature_png, initials_png, consent: true,
      });
      onAdopted(data);
    } catch (e) {
      const m = String(e?.message || "");
      if (m === "nothing-drawn") {
        setErr("That came out blank. Try different wording for your signature.");
      } else {
        setErr(formatApiError(e?.response?.data?.detail) || "Couldn't save your signature.");
      }
    } finally { setBusy(false); }
  };

  const preview = (text, big) => (
    <div style={{
      fontFamily: stackFor(style),
      fontSize: big ? 44 : 30, color: "#17306b", lineHeight: 1.5,
      minHeight: big ? 64 : 48, display: "flex", alignItems: "center",
      overflow: "hidden", whiteSpace: "nowrap",
    }}>{text || <span style={{ ...muted, fontFamily: "inherit" }}>&nbsp;</span>}</div>
  );

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 950, background: "rgba(28,25,23,0.55)",
                  display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
                  overflowY: "auto" }}
         data-testid="adopt-signature">
      <div className="card" style={{ background: "#fff", padding: 28, maxWidth: 560, width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div className="overline">Your signature</div>
            <h2 className="font-serif" style={{ fontSize: 24, fontWeight: 500, margin: "6px 0 0" }}>
              Adopt your signature
            </h2>
          </div>
          <button type="button" className="btn-ghost" style={{ padding: 6 }} onClick={onClose}
                  data-testid="adopt-close" title="Close"><X size={18} /></button>
        </div>
        <p style={{ ...muted, fontSize: 14, marginTop: 8 }}>
          Type your name, pick how it's written, and adopt it. From then on it goes
          wherever your agent has asked you to sign or initial.
        </p>

        <label style={{ fontSize: 12, ...muted }}>Full name</label>
        <input className="input-field" value={name} data-testid="adopt-name"
               onChange={(e) => onName(e.target.value)} maxLength={120}
               style={{ width: "100%", padding: "9px 11px", marginBottom: 12 }} />

        <label style={{ fontSize: 12, ...muted }}>Initials</label>
        <input className="input-field" value={initials} data-testid="adopt-initials"
               onChange={(e) => { touched.current = true; setInitials(e.target.value); }}
               maxLength={12}
               style={{ width: 120, padding: "9px 11px", marginBottom: 14 }} />

        <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
          {Object.entries(STYLES).map(([k, s]) => (
            <button key={k} type="button" onClick={() => setStyle(k)}
                    data-testid={`adopt-style-${k}`}
                    style={{ padding: "6px 12px", borderRadius: 999, fontSize: 12.5, cursor: "pointer",
                             border: `1px solid ${style === k ? "var(--primary)" : "var(--border)"}`,
                             background: style === k ? "var(--primary)" : "#fff",
                             color: style === k ? "#fff" : "var(--text)" }}>
              {s.label}
            </button>
          ))}
        </div>

        <div style={{ border: "1px solid var(--border)", borderRadius: 10, padding: "14px 18px",
                      background: "var(--surface-alt)", marginBottom: 16 }}
             data-testid="adopt-preview">
          {noCursive ? (
            <div style={{ fontSize: 13.5, lineHeight: 1.55 }} data-testid="adopt-no-cursive">
              <b style={{ color: "var(--secondary)" }}>The signature lettering didn&rsquo;t load.</b>
              <div style={{ ...muted, marginTop: 4 }}>
                Signing now would put your name in ordinary type rather than handwriting,
                so it&rsquo;s paused here. This is usually a network blocking web fonts.
              </div>
              <button type="button" className="btn-secondary" data-testid="adopt-retry"
                      onClick={() => setAttempt((n) => n + 1)}
                      style={{ marginTop: 10, fontSize: 13 }}>Try again</button>
            </div>
          ) : !ready ? (
            <div style={{ ...muted, fontSize: 13.5, display: "flex", alignItems: "center", gap: 8 }}>
              <Loader2 size={14} /> Loading the lettering&hellip;
            </div>
          ) : (
            <>
              <div style={{ fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase",
                            ...muted, marginBottom: 2 }}>Signature</div>
              {preview(name, true)}
              <div style={{ fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase",
                            ...muted, marginTop: 10, marginBottom: 2 }}>Initials</div>
              {preview(initials, false)}
            </>
          )}
        </div>

        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13,
                        lineHeight: 1.55, cursor: "pointer", marginBottom: 16 }}>
          <input type="checkbox" checked={agreed} data-testid="adopt-consent"
                 onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: 3 }} />
          <span>{consentText}</span>
        </label>

        {err && (
          <p style={{ color: "var(--secondary)", fontSize: 13.5, marginTop: 0 }} data-testid="adopt-error">
            {err}
          </p>
        )}

        <button type="button" className="btn-primary" onClick={adopt} data-testid="adopt-submit"
                disabled={busy || !ready || !agreed || name.trim().length < 2}
                style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
          <PenLine size={14} /> {busy ? "Saving…" : "Adopt and continue"}
        </button>
      </div>
    </div>
  );
}

/* A placed signature or set of initials, drawn into the box the client tapped.

   This is a preview of what the PDF will say, so it has to stay inside the
   box the agent drew. A font size picked from the box's height alone would
   run a long name straight out the side, so the text is measured once and
   squeezed horizontally to fit -- which is also what happens to the stamped
   PNG when reportlab fits it to the same rectangle. */
function Ink({ text, style, w, h }) {
  const ref = useRef(null);
  const [squeeze, setSqueeze] = useState(1);

  useLayoutEffect(() => {
    const el = ref.current;
    // scrollWidth is the laid-out width and a transform does not change it,
    // so measuring again after a squeeze still reports the natural width.
    if (!el || !el.scrollWidth) return;
    setSqueeze(Math.min(1, Math.max(0.2, (w - 3) / el.scrollWidth)));
  }, [text, style, w, h]);

  return (
    <span ref={ref} data-testid="sign-ink"
          style={{ fontFamily: stackFor(style), fontSize: Math.max(8, h * 0.8),
                   lineHeight: 1.05, color: "#17306b", whiteSpace: "nowrap",
                   display: "inline-block", paddingLeft: 1,
                   transform: `scaleX(${squeeze})`, transformOrigin: "left bottom" }}>
      {text}
    </span>
  );
}

/* ------------------------------------------------ filling in and signing */

export default function SignForm({ templateId, onClose, onSigned }) {
  const [form, setForm] = useState(null);
  const [sig, setSig] = useState(null);
  const [answers, setAnswers] = useState({});
  const [page, setPage] = useState(0);
  const [imgUrl, setImgUrl] = useState("");
  const [imgErr, setImgErr] = useState("");
  const [k, setK] = useState(0);
  const [adopting, setAdopting] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null);
  // field_id -> the client has put their mark in that box.
  const [placed, setPlaced] = useState({});
  // The box they tapped before they had a signature to put in it.
  const [pending, setPending] = useState("");

  const imgRef = useRef(null);
  const cache = useRef({});

  useEffect(() => () => {
    Object.values(cache.current).forEach((u) => URL.revokeObjectURL(u));
  }, []);

  const load = useCallback(async () => {
    try {
      const [f, s] = await Promise.all([
        api.get(`/me/form/${templateId}`),
        api.get("/me/signature"),
      ]);
      setForm(f.data);
      setSig(s.data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Couldn't open that form.");
    }
  }, [templateId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    setImgErr("");
    if (cache.current[page]) { setImgUrl(cache.current[page]); return; }
    setImgUrl("");
    (async () => {
      try {
        const { data } = await api.get(`/me/form/${templateId}/page/${page}.png`,
                                       { responseType: "blob", timeout: 60000 });
        const url = URL.createObjectURL(data);
        if (!alive) { URL.revokeObjectURL(url); return; }
        cache.current[page] = url;
        setImgUrl(url);
      } catch (e) {
        if (alive) setImgErr(formatApiError(e?.response?.data?.detail) || "Couldn't show that page.");
      }
    })();
    return () => { alive = false; };
  }, [templateId, page]);

  const size = (form?.page_sizes || [])[page];
  const pw = size ? size[0] : 0;
  const ph = size ? size[1] : 0;

  const measure = useCallback(() => {
    const el = imgRef.current;
    if (!el || !pw) return;
    if (el.clientWidth > 0) setK(el.clientWidth / pw);
  }, [pw]);

  useEffect(() => {
    measure();
    const el = imgRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measure, imgUrl]);

  const submit = async () => {
    setErr("");
    if (!agreed) { setErr("Please tick the box to agree before signing."); return; }
    const missing = (form.questions || []).filter(
      (q) => q.kind !== "check" && !String(answers[q.field_id] || "").trim());
    if (missing.length) {
      setErr(`Still to fill in: ${missing.map((m) => m.label || "a field").join(", ")}`);
      return;
    }
    // Recomputed here rather than read from the render: this runs on a
    // click, and the button is not the only way in.
    const todo = (form.marks || []).filter(
      (m) => (m.kind === "signature" || m.kind === "initial") && !placed[m.field_id]);
    if (todo.length) {
      setErr(`${todo.length} box${todo.length === 1 ? "" : "es"} still need`
             + `${todo.length === 1 ? "s" : ""} your signature or initials \u2014 tap them on the page.`);
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/me/form/${templateId}/sign`,
                                      { answers, consent: true,
                                        placed: Object.keys(placed).filter((id) => placed[id]) });
      setDone(data);
      onSigned && onSigned(data);
    } catch (e) {
      setErr(formatApiError(e?.response?.data?.detail) || "Couldn't sign that form.");
    } finally { setBusy(false); }
  };

  const shell = (children) => (
    <div style={{ position: "fixed", inset: 0, zIndex: 900, background: "rgba(28,25,23,0.55)",
                  display: "flex", justifyContent: "center", padding: 16, overflowY: "auto" }}
         data-testid="sign-form">
      <div style={{ background: "var(--surface-alt)", borderRadius: 14, width: "100%",
                    maxWidth: 1020, alignSelf: "flex-start", overflow: "hidden" }}>
        {children}
      </div>
    </div>
  );

  if (err && !form) {
    return shell(
      <div style={{ padding: 28, background: "#fff" }}>
        <p style={{ color: "var(--secondary)", margin: 0 }}>{err}</p>
        <button type="button" className="btn-secondary" onClick={onClose}
                style={{ marginTop: 14 }}>Close</button>
      </div>
    );
  }
  if (!form) return shell(<div style={{ padding: 28, ...muted }}>Loading&hellip;</div>);

  if (done) {
    return shell(
      <div style={{ padding: 32, background: "#fff", textAlign: "center" }} data-testid="sign-done">
        <Check size={30} style={{ color: "var(--primary)" }} />
        <h2 className="font-serif" style={{ fontSize: 26, fontWeight: 500, margin: "12px 0 6px" }}>
          Signed
        </h2>
        <p style={{ ...muted, maxWidth: 460, margin: "0 auto 20px" }}>
          {form.name} is signed and saved to your documents, with a certificate page
          recording when and how. Your agent has been told.
        </p>
        <button type="button" className="btn-primary" onClick={onClose} data-testid="sign-done-close">
          Done
        </button>
      </div>
    );
  }

  const marks = (form.marks || []).filter((m) => m.page === page);
  const pages = form.page_count || 0;

  const inkMarks = (form.marks || []).filter(
    (m) => m.kind === "signature" || m.kind === "initial");
  const left = inkMarks.filter((m) => !placed[m.field_id]).length;
  // Reading order, so "the next one" is the next one they would come to.
  const nextUp = [...inkMarks]
    .sort((a, b) => a.page - b.page || b.rect.y - a.rect.y || a.rect.x - b.rect.x)
    .find((m) => !placed[m.field_id]) || null;

  const tap = (m) => {
    // Tapping a box before adopting a signature is how most people will
    // start, so that tap opens the adopt dialog and is honoured afterwards
    // rather than being thrown away.
    if (!sig?.adopted) { setPending(m.field_id); setAdopting(true); return; }
    setPlaced((p) => ({ ...p, [m.field_id]: !p[m.field_id] }));
  };

  const goNext = () => {
    if (!nextUp) return;
    const turning = nextUp.page !== page;
    if (turning) setPage(nextUp.page);
    // The box is often below the fold, and after a page turn the image has
    // to arrive before there is anything to scroll to.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="sign-mark-${nextUp.field_id}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, turning ? 700 : 0);
  };

  return shell(
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 18px",
                    borderBottom: "1px solid var(--border)", background: "#fff", flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: "1 1 220px" }}>
          <div className="overline">To sign</div>
          <div className="font-serif" style={{ fontSize: 19, fontWeight: 500 }}>{form.name}</div>
        </div>
        {pages > 1 && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button type="button" className="btn-ghost" style={{ padding: 6 }} disabled={page === 0}
                    data-testid="sign-prev" onClick={() => setPage((n) => Math.max(0, n - 1))}>
              <ChevronLeft size={16} />
            </button>
            <span style={{ fontSize: 13, ...muted, minWidth: 92, textAlign: "center" }}>
              Page {page + 1} of {pages}
            </span>
            <button type="button" className="btn-ghost" style={{ padding: 6 }} disabled={page >= pages - 1}
                    data-testid="sign-next" onClick={() => setPage((n) => Math.min(pages - 1, n + 1))}>
              <ChevronRight size={16} />
            </button>
          </div>
        )}
        <button type="button" className="btn-ghost" style={{ padding: 6 }} onClick={onClose}
                data-testid="sign-close" title="Close"><X size={18} /></button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ flex: "1 1 520px", minWidth: 0, padding: 20 }}>
          <div style={{ position: "relative", margin: "0 auto", maxWidth: 740,
                        boxShadow: "0 2px 18px rgba(0,0,0,0.12)", background: "#fff" }}>
            {imgErr ? (
              <div style={{ padding: 28, color: "var(--secondary)", fontSize: 13.5 }}>{imgErr}</div>
            ) : !imgUrl ? (
              <div style={{ padding: 28, ...muted }}>Showing page {page + 1}&hellip;</div>
            ) : (
              <>
                <img ref={imgRef} src={imgUrl} alt={`Page ${page + 1}`} onLoad={measure}
                     data-testid="sign-page-img"
                     style={{ display: "block", width: "100%", height: "auto" }} />
                {k > 0 && marks.map((m) => {
                  const inkable = m.kind === "signature" || m.kind === "initial";
                  const on = !!placed[m.field_id];
                  const next = nextUp && nextUp.field_id === m.field_id;
                  const bw = m.rect.w * k;
                  const bh = m.rect.h * k;
                  const verb = m.kind === "initial" ? "initial" : "sign";
                  return (
                    <div key={m.field_id} data-testid={`sign-mark-${m.field_id}`}
                         onClick={inkable ? () => tap(m) : undefined}
                         title={inkable
                           ? (on ? "Tap to take it back out" : `Tap to ${verb} here`)
                           : (m.label || m.kind)}
                         style={{ position: "absolute", left: m.rect.x * k,
                                  top: (ph - m.rect.y - m.rect.h) * k,
                                  width: bw, height: bh, boxSizing: "border-box",
                                  display: "flex", alignItems: "flex-end",
                                  overflow: "hidden",
                                  border: on
                                    ? "1.5px solid transparent"
                                    : `1.5px ${inkable ? "dashed" : "solid"} var(--secondary)`,
                                  background: on ? "transparent"
                                    : next ? "rgba(184,92,71,0.30)" : "rgba(184,92,71,0.14)",
                                  boxShadow: next ? "0 0 0 3px rgba(184,92,71,0.35)" : "none",
                                  cursor: inkable ? "pointer" : "default",
                                  // Only the tappable ones take the click; an
                                  // answer box is just a marker and must not
                                  // swallow a tap meant for the page.
                                  pointerEvents: inkable ? "auto" : "none" }}>
                      {on && sig && (
                        <Ink text={m.kind === "initial" ? sig.initials : sig.name}
                             style={sig.style} w={bw} h={bh} />
                      )}
                      {!on && inkable && bh > 12 && (
                        <span style={{ fontSize: Math.min(10, bh * 0.5), lineHeight: 1,
                                       color: "#fff", background: "var(--secondary)",
                                       padding: "1px 4px", borderRadius: 3, margin: 1,
                                       whiteSpace: "nowrap" }}>
                          {m.kind === "initial" ? "Initial" : "Sign"}
                        </span>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
          <p style={{ ...muted, fontSize: 12, textAlign: "center", marginTop: 12 }}>
            Tap each dashed box to sign or initial there. Solid boxes are where
            your typed answers will go.
          </p>
        </div>

        <div style={{ flex: "0 0 340px", padding: 20, borderLeft: "1px solid var(--border)",
                      background: "#fff", alignSelf: "stretch" }}>
          {(form.questions || []).length > 0 && (
            <>
              <div className="overline" style={{ marginBottom: 10 }}>Your answers</div>
              {form.questions.map((q) => (
                <div key={q.field_id} style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, ...muted }}>
                    {q.label || "Answer"} <span style={{ opacity: 0.6 }}>&middot; p{q.page + 1}</span>
                  </label>
                  {q.kind === "check" ? (
                    <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13.5,
                                    marginTop: 4, cursor: "pointer" }}>
                      <input type="checkbox" data-testid={`sign-q-${q.field_id}`}
                             checked={!!answers[q.field_id]}
                             onChange={(e) => setAnswers((a) => ({
                               ...a, [q.field_id]: e.target.checked ? "yes" : "" }))} />
                      Yes
                    </label>
                  ) : q.kind === "multiline" ? (
                    <textarea className="input-field" rows={3} data-testid={`sign-q-${q.field_id}`}
                              value={answers[q.field_id] || ""} maxLength={300}
                              onChange={(e) => setAnswers((a) => ({ ...a, [q.field_id]: e.target.value }))}
                              style={{ width: "100%", padding: "8px 10px", fontSize: 13.5 }} />
                  ) : (
                    <input className="input-field" data-testid={`sign-q-${q.field_id}`}
                           value={answers[q.field_id] || ""} maxLength={300}
                           onChange={(e) => setAnswers((a) => ({ ...a, [q.field_id]: e.target.value }))}
                           style={{ width: "100%", padding: "8px 10px", fontSize: 13.5 }} />
                  )}
                </div>
              ))}
            </>
          )}

          <div className="overline" style={{ margin: "18px 0 8px" }}>Your signature</div>
          {sig?.adopted ? (
            <div style={{ border: "1px solid var(--border)", borderRadius: 10, padding: "12px 14px",
                          background: "var(--surface-alt)", marginBottom: 14 }}
                 data-testid="sign-have-signature">
              {/* Shown with the webfont rather than by fetching the stored PNG:
                  an <img> can't carry the auth header, so that request would
                  just fail quietly. Same face, same words, no broken request. */}
              <div style={{ fontFamily: stackFor(sig.style),
                            fontSize: 32, color: "#17306b", lineHeight: 1.4 }}>
                {sig.name}
              </div>
              <div style={{ ...muted, fontSize: 12, marginTop: 4 }}>
                Initials {sig.initials} &middot;{" "}
                <button type="button" onClick={() => setAdopting(true)} data-testid="sign-change-signature"
                        style={{ background: "none", border: 0, padding: 0, cursor: "pointer",
                                 color: "var(--secondary)", textDecoration: "underline", fontSize: 12 }}>
                  change it
                </button>
              </div>
            </div>
          ) : (
            <button type="button" className="btn-secondary" onClick={() => setAdopting(true)}
                    data-testid="sign-adopt-first"
                    style={{ marginBottom: 14, display: "inline-flex", alignItems: "center", gap: 7 }}>
              <PenLine size={14} /> Create your signature
            </button>
          )}

          {inkMarks.length > 0 && (
            <div style={{ border: "1px solid var(--border)", borderRadius: 10,
                          padding: "11px 13px", marginBottom: 4 }}
                 data-testid="sign-progress">
              <div style={{ fontSize: 13 }}>
                {inkMarks.length - left} of {inkMarks.length} placed
              </div>
              <p style={{ ...muted, fontSize: 12, margin: "4px 0 0", lineHeight: 1.5 }}>
                {left === 0
                  ? "Every box is signed. Agree below and send."
                  : "Tap each dashed box on the page to put your signature or "
                    + "initials there."}
              </p>
              {left > 0 && (
                <button type="button" className="btn-secondary" onClick={goNext}
                        data-testid="sign-goto-next"
                        style={{ marginTop: 9, display: "inline-flex", alignItems: "center",
                                 gap: 6, fontSize: 12.5, padding: "6px 11px" }}>
                  Take me to the next one <ArrowRight size={13} />
                </button>
              )}
            </div>
          )}

          <label style={{ display: "flex", gap: 9, alignItems: "flex-start", fontSize: 12.5,
                          lineHeight: 1.55, cursor: "pointer", margin: "14px 0" }}>
            <input type="checkbox" checked={agreed} data-testid="sign-consent"
                   onChange={(e) => setAgreed(e.target.checked)} style={{ marginTop: 3 }} />
            <span>{form.consent_text}</span>
          </label>

          {err && <p style={{ color: "var(--secondary)", fontSize: 13 }} data-testid="sign-error">{err}</p>}

          <button type="button" className="btn-primary" onClick={submit} data-testid="sign-submit"
                  disabled={busy || !agreed || !sig?.adopted || left > 0}
                  style={{ width: "100%", display: "inline-flex", alignItems: "center",
                           justifyContent: "center", gap: 7 }}>
            <FileText size={14} /> {busy ? "Signing…" : "Sign and send"}
          </button>
          {form.already_signed && (
            <p style={{ ...muted, fontSize: 12, marginTop: 10 }} data-testid="sign-again-warning">
              You signed this once already. Signing again makes a second signed copy;
              the first one stays as it was.
            </p>
          )}
        </div>
      </div>

      {adopting && (
        <AdoptSignature
          suggestedName={sig?.name || sig?.suggested_name || ""}
          consentText={sig?.consent_text || ""}
          onClose={() => { setAdopting(false); setPending(""); }}
          onAdopted={(d) => {
            setAdopting(false);
            setSig({ ...sig, ...d, adopted: true });
            if (pending) {
              setPlaced((p) => ({ ...p, [pending]: true }));
              setPending("");
            }
          }}
        />
      )}
    </>
  );
}
